import { useState, useContext } from "react";
import { getCategories, SettingsCategory, SETTINGS_STRUCTURE } from "./constants/settingsStructure";
import CategoryLayout from "./CategoryLayout";
import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import { ExternalLink, Settings } from "lucide-react";
import BryntumContext from "@/react/context/bryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";

const SettingsView = () => {
  const categories = getCategories();
  const [selectedCategory, setSelectedCategory] = useState<SettingsCategory>(SETTINGS_STRUCTURE.aiPrompt);
  const { editorHandler } = useContext(BryntumContext);

  const handleOpenLegacySettings = (settingsType: "general" | "personal") => {
    const editorKey = settingsType === "general"
      ? EDITOR_KEYS.settingGeneralEditor
      : EDITOR_KEYS.settingPersonalEditor;

    editorHandler.openEditor(
      editorKey,
      null,
      {},
      { useExisting: true, by: "recordType" }
    );
  };

  return (
    <div className="flex h-full w-full">
      {/* Left Panel - Categories Sidebar */}
      <div className="w-64 border-r border-border overflow-y-auto bg-card/50 dark:bg-card">
        <div className="p-4 border-b border-border h-[60px] flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">Settings</h2>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                title="Open Legacy Settings Editor"
                className="h-8 w-8 p-0"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Legacy Settings
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleOpenLegacySettings("general")} className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                <span>Board Settings</span>
                <ExternalLink className="ml-auto h-3 w-3 opacity-50" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleOpenLegacySettings("personal")} className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                <span>Personal Settings</span>
                <ExternalLink className="ml-auto h-3 w-3 opacity-50" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <ul>
          {categories.map((category) => (
            <li
              key={category.id}
              onClick={() => setSelectedCategory(category)}
              className={cn(
                "p-4 cursor-pointer hover:bg-accent/50 flex items-center gap-3 transition-colors border-b border-border relative",
                selectedCategory.id === category.id && "bg-blue-50 dark:bg-blue-900/20 before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-blue-500"
              )}
            >
              <i className={`${category.icon} text-muted-foreground`} />
              <span className="font-medium text-foreground">{category.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Middle + Right Panels */}
      <div className="flex-1 min-w-0">
        <CategoryLayout category={selectedCategory} />
      </div>
    </div>
  );
};

export default SettingsView;
